import React from 'react';
import { Box } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { SparkDropdown, SparkTextField } from 'sharedComponents/SparkForm';
import StepReferenceTextField from 'components/CreateSpark/StepReferenceTextField';
import PromptFunctionConfigSection from './PromptFunctionConfigSection';
import type { PromptFunctionsFormValueType } from 'types/PromptFunctions';
import { DropdownOption } from 'types/common/DropdownOption';
import { SettingsIcon } from 'components/SVG';
import { SparkReferenceValue } from 'types/SparkStepReference';
import type { AttachmentsType } from '.';
import { useSparkBuilderStore } from 'stores';
import { STEP_TYPE_NAMES } from 'constants/SparkSteps';
import { isJSON } from 'utilities/index';

type Props = {
  onProviderChange: (providerId: string | number) => void;
  onChangeAiModel: (aiModelId: string | number) => void;
  outputDropdownOptions: DropdownOption[];
};

function PromptFunctionFormContent({
  onProviderChange,
  onChangeAiModel,
  outputDropdownOptions,
}: Props) {
  const { values, setFieldValue } =
    useFormikContext<PromptFunctionsFormValueType>();
  const [field] = useField('attachments');
  const selectedStep = useSparkBuilderStore(
    (store) => store.builderSideBar.selectedStep
  );
  const sparkStepTypeIdMap = useSparkBuilderStore(
    (store) => store.stepTypeIdMap
  );

  const sparkStep = useSparkBuilderStore((store) =>
    store.sparkStepsActions.getSparkStep(selectedStep?.id || '')
  );

  const functionType =
    sparkStepTypeIdMap?.[sparkStep?.attributes.sparkStepTypeID || ''];
  const functionName = functionType?.attributes.key || '';

  const otherDropDownOption = React.useMemo(() => {
    return {
      value: 'custom',
      label: 'Custom',
      icon: (
        <SettingsIcon stroke='black' width='20' height='20' strokeWidth='2.5' />
      ),
    };
  }, []);

  const allDropDownOptions = [...outputDropdownOptions, otherDropDownOption];

  const handleFilesReference = (reference: SparkReferenceValue) => {
    if (functionName === STEP_TYPE_NAMES.MEMORY) {
      return;
    }

    const formattedID = reference.reference;
    const alreadyExists =
      values.attachments &&
      values.attachments.some(
        (item: AttachmentsType) => item.id === formattedID
      );

    if (alreadyExists) {
      return;
    }

    const value = reference.value;
    const parsedValue = isJSON(value) ? JSON.parse(value) : value;
    const isNoTestValue = value === 'No test value';
    if (
      Array.isArray(parsedValue) &&
      parsedValue &&
      parsedValue.length > 0 &&
      parsedValue[0].name
    ) {
      const fileName = parsedValue[0].name;
      const fileType = parsedValue[0].type;

      const lastDotIndex = fileName.lastIndexOf('.');
      const fileTypeTag = fileName.substring(lastDotIndex + 1).toLowerCase();

      let typeDetails = 'Document';
      if (fileType.startsWith('image/')) {
        typeDetails = 'Image';
      }

      setFieldValue('attachments', [
        ...values.attachments,
        {
          id: reference.reference,
          title: fileName,
          type: 'file',
          details: typeDetails,
          fileTypeTag: fileTypeTag,
          thumbnail: reference.iconURL,
        },
      ]);
    } else {
      setFieldValue('attachments', [
        ...values.attachments,
        {
          id: reference.reference,
          title: reference.key,
          type: 'file',
          details: parsedValue,
          fileTypeTag: isNoTestValue ? 'warning' : '',
          thumbnail: reference.iconURL,
        },
      ]);
    }
  };

  const handleAttachmentRemove = (
    cardType: AttachmentsType['type'],
    actionKey: string
  ) => {
    const attachment = field.value.filter(
      (attachment: AttachmentsType) => attachment.id !== actionKey
    );

    if (attachment.length > 0) {
      setFieldValue('attachments', attachment);
      return;
    }
    setFieldValue('attachments', []);
  };

  const attachemets = React.useMemo(() => {
    return field.value;
  }, [field.value]);

  return (
    <Box width='100%'>
      <StepReferenceTextField
        name='prompt'
        label='Prompt'
        attachments={attachemets}
        handleFilesReference={handleFilesReference}
        onAttachmentRemove={handleAttachmentRemove}
      />
      <SparkTextField
        name='instructions'
        label='Instructions'
        multiline={true}
        placeholder='Act as a helpful assistant'
        size={12}
        sx={{
          '& textarea': {
            fontSize: '16px !important',
            maxHeight: '300px !important',
            overflow: 'auto !important',
          },
        }}
        helpText={`Instructions configure the AI's personality and role, allowing it to provide responses consistent with a particular field, like engineering or law.`}
      />
      <SparkDropdown name='outputBoardNodeTypeID' label='Output' size={12}>
        {allDropDownOptions || []}
      </SparkDropdown>
      {values.outputBoardNodeTypeID === 'custom' && (
        <PromptFunctionConfigSection
          onProviderChange={onProviderChange}
          onChangeAiModel={onChangeAiModel}
        />
      )}
    </Box>
  );
}

export default PromptFunctionFormContent;
